import React, { useState, useEffect, useMemo } from 'react';
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Typography,
  Checkbox,
  IconButton,
  Button,
  CircularProgress,
  Dialog,
  DialogContent,
  Box,
  Grid,
  TextField,
  LinearProgress,
} from '@material-ui/core';
import InfoIcon from '@material-ui/icons/Info';
import CancelIcon from '@mui/icons-material/Cancel';
import SaveIcon from '@mui/icons-material/Save';
import CloseIcon from '@mui/icons-material/Close';
import '../../assets/styles/ComparablesTable/ComparablesTable.css';
import DetailedHomeView from '../DetailedHomeView';
import CompareModal from '../CompareModal';
import { comparisonFields } from "../utils/fields";
import { getDisplayAddress } from '../utils/utils';
import Carousel from 'react-material-ui-carousel';
import LazyLoad from 'react-lazyload';
import api, {consoleDevLog} from "../../apiConfig";
import { getFriendlyStatus } from "./Helpers";
import noPhotoPlaceholder from "./no-photo-or-blank-image-icon-loading-images-or-missing-image-mark-image-not-available-or-image-coming-soon-sign-simple-nature-silhouette-in-frame-isolated-illustration-vector.jpg";

const ComparablesTable = ({
  subjectProperty,
  finalSelectedHomes,
  adjustments,
  setAdjustments,
  toggleDrawer,
  setFinalSelectedHomes,
  onHomeDelete,
  loading,
  setCurrentStep,
  step = 2,
}) => {
  const [selectedHome, setSelectedHome] = useState(null);
  const [isModalOpen, setModalOpen] = useState(false);
  const [isCompareModalOpen, setCompareModalOpen] = useState(false);
  const [comparableHome, setComparableHome] = useState(null);
  const [subjectAdjustedPrice, setSubjectAdjustedPrice] = useState(subjectProperty.Lp_dol);
  const [selectedImage, setSelectedImage] = useState(null);
  const [currentImageIndexes, setCurrentImageIndexes] = useState({});
  const [imagesByHome, setImagesByHome] = useState({});
  const [loadingImages, setLoadingImages] = useState(true);
  const noImageArray = [noPhotoPlaceholder];

  // New state variable for custom adjustments inputs
  const [customAdjustmentInputs, setCustomAdjustmentInputs] = useState({});

  const LEFT_COLUMN_WIDTH = 180;
  const MIN_COLUMN_WIDTH = 240; // Minimum width for each comparable column
  const totalColumns = finalSelectedHomes.length + 2;
  const calculatedColumnWidth = `calc((100% - ${LEFT_COLUMN_WIDTH}px) / ${totalColumns})`;

  // Helper: Render the locked property info cell (address + buttons)
  const renderPropertyInfoCell = (home, isSubject = false) => {
    return (
      <div className="address-and-button-container" style={{ position: 'relative', padding: '8px' }}>
        <Typography variant="h6" className="address-text">
          {getDisplayAddress(home)}
        </Typography> 
        {isSubject ? (
          <Button
            variant="contained"
            color="primary"
            className="change-subject-button"
            onClick={() => setCurrentStep(1)}
            style={{ marginTop: 4 }}
          >
            Change Subject
          </Button>
        ) : (
          <Button
            variant="contained"
            color="primary"
            className="compare-button"
            onClick={() => handleCompareClick(home)}
            style={{ marginTop: 4 }}
          >
            Compare
          </Button>
        )}
        <IconButton
          className="info-button"
          onClick={() => handleExpandClick(home)}
          style={{ position: 'absolute', top: 8, left: 8 }}
        >
          <InfoIcon />
        </IconButton>
        {!isSubject && step !== 3 && (
          <IconButton
            className="delete-button"
            onClick={() => handleDeleteComparable(home.id)}
            style={{ position: 'absolute', top: 8, right: 8 }}
          >
            <CancelIcon />
          </IconButton>
        )}
      </div>
    );
  };

  // Helper: Render the image carousel and thumbnails for a property
  const renderImagesCell = (home) => (
    <div>
      <div className="image-container">
        {loadingImages ? (
          <CircularProgress size={24} />
        ) : (
          <Carousel
            autoPlay={false}
            navButtonsAlwaysVisible={
              (home.imageUrls?.length > 1) ||
              (imagesByHome[home.Ml_num]?.length > 1)
            }
            indicators={false}
            className="carousel-container"
            index={currentImageIndexes[home.Ml_num] || 0}
            onChange={(newIndex) => handleCarouselChange(home.Ml_num, newIndex)}
          >
            {((home.imageUrls?.length > 0 ? home.imageUrls : imagesByHome[home.Ml_num]) || noImageArray)
                .map((image, index) => (
              <LazyLoad key={index} height={300} offset={100} once>
                <img
                  src={image}
                  alt={`Property Image ${index + 1}`}
                  className="carousel-image"
                  onClick={() => handleImageClick(image)}
                  style={{ width: '100%', height: '300px', objectFit: 'cover' }}
                />
              </LazyLoad>
            ))}
          </Carousel>
        )}
      </div>
      <Box className="thumbnail-wrapper">
        <Grid container wrap="nowrap">
            {((home.imageUrls?.length > 0 ? home.imageUrls : imagesByHome[home.Ml_num]) || noImageArray)
        .map((image, index) => (
            <Grid item key={index}>
              <img
                src={image}
                alt={`Thumbnail ${index + 1}`}
                className={`thumbnail-image ${currentImageIndexes[home.Ml_num] === index ? 'active-thumbnail' : ''}`}
                onClick={() => handleCarouselChange(home.Ml_num, index)}
                style={{ width: 50, height: 50, objectFit: 'cover' }}
              />
            </Grid>
          ))}
        </Grid>
      </Box>
    </div>
  );

  // Utility function to calculate the adjusted price for a single property
  const calculateAdjustedPrice = (home, adjustments) => {
    const homeAdjustments = adjustments[home.Ml_num] || [];
    return homeAdjustments.reduce(
      (sum, adj) => sum + (adj.selected ? adj.amount : 0),
      home.Sp_dol
    );
  };

  // Utility function to calculate the average adjusted price for all comparables
  const calculateAverageAdjustedPrice = (homes, adjustments) => {
    const adjustedPrices = homes.map((home) => calculateAdjustedPrice(home, adjustments));
    if (adjustedPrices.length === 0) return 0; // Avoid division by zero
    return adjustedPrices.reduce((sum, price) => sum + price, 0) / adjustedPrices.length;
  };

  // Calculate the initial subjectAdjustedPrice when the component mounts or comparables change
  useEffect(() => {
    const initialAverage = calculateAverageAdjustedPrice(finalSelectedHomes, adjustments);
    setSubjectAdjustedPrice(Math.round(initialAverage / 10) * 10); // Round to the nearest 10
  }, [adjustments, finalSelectedHomes]);

  useEffect(() => {
    const fetchAllImages = async () => {
      setLoadingImages(true);
      const allHomes = [subjectProperty, ...finalSelectedHomes];
      const newImagesByHome = {};

      await Promise.all(
        allHomes.map(async (home) => {
          if (!home) return;
          try {
            const response = await api.get('/images', {
              params: { ml_number: home.Ml_num },
            });
            newImagesByHome[home.Ml_num] = response.data.images.length
              ? response.data.images
              : noImageArray;
          } catch (error) {
            console.error(`Error fetching images for MLS# ${home.Ml_num}:`, error);
          }
        })
      );

      consoleDevLog("New Images By Home:", newImagesByHome);

      setImagesByHome(newImagesByHome);
      setLoadingImages(false);
    };

    fetchAllImages();
  }, [subjectProperty, finalSelectedHomes]);

  const handleAdjustmentSelect = (home, index) => {
    if (setAdjustments) {
      setAdjustments((prev) => {
        const updatedAdjustments = {
          ...prev,
          [home.Ml_num]: prev[home.Ml_num].map((adj, i) =>
            i === index ? { ...adj, selected: !adj.selected } : adj
          ),
        };

        // Update the subject's adjusted price based on new adjustments
        const newAverageAdjustedPrice = calculateAverageAdjustedPrice(finalSelectedHomes, updatedAdjustments);
        setSubjectAdjustedPrice(newAverageAdjustedPrice);

        return updatedAdjustments;
      });
    }
  };

  // Handle starting to add a custom adjustment
  const handleStartAddingCustomAdjustment = (home) => {
    setCustomAdjustmentInputs((prev) => ({
      ...prev,
      [home.Ml_num]: {
        description: '',
        amount: '',
        isAdding: true,
      },
    }));
  };

  // Handle canceling the custom adjustment input
  const handleCancelCustomAdjustment = (home) => {
    setCustomAdjustmentInputs((prev) => ({
      ...prev,
      [home.Ml_num]: {
        ...prev[home.Ml_num],
        isAdding: false,
      },
    }));
  };

  // Handle input changes for custom adjustment
  const handleCustomAdjustmentChange = (home, field, value) => {
    setCustomAdjustmentInputs((prev) => ({
      ...prev,
      [home.Ml_num]: {
        ...prev[home.Ml_num],
        [field]: value,
      },
    }));
  };

  // Handle saving the custom adjustment
  const handleSaveCustomAdjustment = (home) => {
    const input = customAdjustmentInputs[home.Ml_num];
    if (!input || !input.description || !input.amount) {
      // Optionally, you can show an error or validation message
      return;
    }

    const newAdjustment = {
      description: input.description,
      amount: parseFloat(input.amount),
      selected: true,
    };

    setAdjustments((prev) => {
      const homeAdjustments = prev[home.Ml_num] || [];
      const updatedAdjustments = {
        ...prev,
        [home.Ml_num]: [...homeAdjustments, newAdjustment],
      };

      // Update the subject's adjusted price based on new adjustments
      const newAverageAdjustedPrice = calculateAverageAdjustedPrice(finalSelectedHomes, updatedAdjustments);
      setSubjectAdjustedPrice(newAverageAdjustedPrice);

      return updatedAdjustments;
    });

    // Reset the input fields and isAdding
    setCustomAdjustmentInputs((prev) => ({
      ...prev,
      [home.Ml_num]: {
        description: '',
        amount: '',
        isAdding: false,
      },
    }));
  };

  const handleExpandClick = (home) => {
    setSelectedHome(home);
    setModalOpen(true);
  };

  const handleModalClose = () => setModalOpen(false);
  const handleCompareClick = (home) => {
    setComparableHome(home);
    setCompareModalOpen(true);
  };
  const handleCompareModalClose = () => setCompareModalOpen(false);
  const handleDeleteComparable = (homeId) => {
    setFinalSelectedHomes((prevSelectedHomes) => prevSelectedHomes.filter((home) => home.id !== homeId));
    onHomeDelete(homeId);
  };

  const handleImageClick = (image) => setSelectedImage(image);

  const handleCarouselChange = (mlNum, newIndex) => {
    setCurrentImageIndexes((prevIndexes) => ({
      ...prevIndexes,
      [mlNum]: newIndex,
    }));
  };

  // Row renderers for the other rows (e.g. adjustments, sold price, etc.)
  const rowRenderers = {
    0: (subjectProperty, home, adjustments, handleAdjustmentSelect) => {
      if (home.Ml_num === subjectProperty.Ml_num) {
        return "N/A";
      } else if (loading) {
        return <CircularProgress size={24} />;
      } else {
        const customInput = customAdjustmentInputs[home.Ml_num] || {};
        const isAdding = customInput.isAdding;

        return (
          <div className="adjustments-content">
            {(adjustments[home.Ml_num] || []).map((adjustment, index) => (
              <div key={index} className="adjustment-item">
                <Checkbox
                  checked={adjustment.selected || false}
                  onChange={() => handleAdjustmentSelect(home, index)}
                  color="primary"
                />
                {adjustment.description}: {adjustment.amount > 0 ? `+${adjustment.amount.toLocaleString()}` : adjustment.amount.toLocaleString()}
              </div>
            ))}
            {isAdding ? (
              <div className="custom-adjustment-inputs">
                <Grid container alignItems="center" spacing={1}>
                  <Grid item xs={5}>
                    <TextField
                      label="Description"
                      value={customInput.description || ''}
                      onChange={(e) => handleCustomAdjustmentChange(home, 'description', e.target.value)}
                      fullWidth
                      margin="dense"
                    />
                  </Grid>
                  <Grid item xs={4}>
                    <TextField
                      label="Amount"
                      value={customInput.amount || ''}
                      onChange={(e) => handleCustomAdjustmentChange(home, 'amount', e.target.value)}
                      type="number"
                      fullWidth
                      margin="dense"
                    />
                  </Grid>
                  <Grid item xs={3}>
                    <IconButton
                      onClick={() => handleSaveCustomAdjustment(home)}
                      color="primary"
                    >
                      <SaveIcon />
                    </IconButton>
                    <IconButton
                      onClick={() => handleCancelCustomAdjustment(home)}
                      color="secondary"
                    >
                      <CloseIcon />
                    </IconButton>
                  </Grid>
                </Grid>
              </div>
            ) : (
              <Button
                onClick={() => handleStartAddingCustomAdjustment(home)}
                color="primary"
                variant="outlined"
                style={{ marginTop: '10px', width: '100%' }}
              >
                + Custom Adjustment
              </Button>
            )}
          </div>
        );
      }
    },
    1: (subjectProperty, home, adjustments) => {
      if (subjectProperty.Ml_num === home.Ml_num) {
        return subjectAdjustedPrice
          ? `$${subjectAdjustedPrice.toLocaleString()}`
          : "Calculating...";
      }
      const adjustedPrice = calculateAdjustedPrice(home, adjustments);
      return `$${adjustedPrice.toLocaleString()}`;
    },
    2: (subjectProperty, home) => home.Sp_dol ? `$${home.Sp_dol.toLocaleString()}` : 'N/A',
    3: (subjectProperty, home) => home.Lp_dol ? `$${home.Lp_dol.toLocaleString()}` : 'N/A',
    4: (subjectProperty, home) => getFriendlyStatus(home.Lsc),
    5: (subjectProperty, home) => home.Sqft ? home.Sqft.toLocaleString() : "N/A",
    6: (subjectProperty, home) => home.Br ? home.Br.toLocaleString() : "N/A",
    7: (subjectProperty, home) => home.Bath_tot ? home.Bath_tot.toLocaleString() : "N/A",
    8: (subjectProperty, home) =>
      home.Front_ft && home.Depth
        ? `${Math.round(home.Front_ft * home.Depth).toLocaleString()} sqft`
        : "N/A",
  };

  const rows = [
    (loading || (adjustments && Object.keys(adjustments).length > 0)) && {
      label: 'Suggested Price Adjustments',
      render: (subjectProperty, home) =>
        rowRenderers[0](subjectProperty, home, adjustments, handleAdjustmentSelect),
    },
    step === 3 && { label: 'Adjusted Price', render: rowRenderers[1] },
    { label: 'Sold Price', render: rowRenderers[2] },
    { label: 'Original Price', render: rowRenderers[3] },
    {
      label: 'Relevance Score',
      render: (subjectProperty, home) => {
        if (home.Ml_num === subjectProperty.Ml_num) {
          return <Typography variant="body2">N/A</Typography>;
        }
        return (
          <div>
            <Typography variant="body2" style={{ marginBottom: 4 }}>
              {home.score !== undefined ? home.score.toFixed(2) : 'N/A'}
            </Typography>
            <LinearProgress
              variant="determinate"
              value={home.score || 0}
              style={{ height: 6, borderRadius: 3 }}
            />
          </div>
        );
      },
    },
    { label: 'Status', render: rowRenderers[4] },
    { label: 'Square Feet', render: rowRenderers[5] },
    { label: 'Bedrooms', render: rowRenderers[6] },
    { label: 'Bathrooms', render: rowRenderers[7] },
    { label: 'Lot Size', render: rowRenderers[8] },
  ].filter(Boolean);

  const finalSubjectPropertyImageArray = useMemo(() => {
    if (imagesByHome[subjectProperty.Ml_num]) {
      return imagesByHome[subjectProperty.Ml_num];
    } else if (imagesByHome['no_ml_num']) {
      return imagesByHome['no_ml_num'];
    } else {
      return noImageArray;
    }
  }, [imagesByHome, subjectProperty.Ml_num]);

  return (
    <div className="table-plus-container">
      <TableContainer component={Paper} className="table-container" style={{ maxHeight: "1000px", overflowX: 'unset' }}>
        <Table
          aria-label="comparables table"
          // style={{ minWidth: `${LEFT_COLUMN_WIDTH + MIN_COLUMN_WIDTH * totalColumns}px` }}
        >
          {/* Locked Header Row: Property Info Only */}
          <TableHead>
            <TableRow>
              <TableCell className="left-column-cell" style={{ width: '180px', zIndex: 4, backgroundColor: 'white' }}>
                <Typography variant="body1" className="row-heading">Property</Typography>
              </TableCell>
              <TableCell
                className="subject-property-column"
                style={{ left: "180px", zIndex: 5, backgroundColor: 'lavender' }}
              >
                {renderPropertyInfoCell(subjectProperty, true)}
              </TableCell>
              {finalSelectedHomes.map((home) => (
                <TableCell key={home.id} style={{ backgroundColor: 'white' }}>
                  {renderPropertyInfoCell(home)}
                </TableCell>
              ))}
            </TableRow>
          </TableHead>

          {/* Table Body */}
          <TableBody>
            {/* New Images Row */}
            <TableRow>
              <TableCell className="left-column-cell">
                <Typography variant="body1" className="row-heading">Images</Typography>
              </TableCell>
              <TableCell className="subject-property-column" style={{ backgroundColor: 'lavender' }}>
                {renderImagesCell(subjectProperty)}
              </TableCell>
              {finalSelectedHomes.map((home) => (
                <TableCell key={home.id}>
                  {renderImagesCell(home)}
                </TableCell>
              ))}
            </TableRow>

            {/* Other Data Rows */}
            {rows.map((row, rowIndex) => (
              <TableRow
                key={rowIndex}
                style={{
                  backgroundColor: rowIndex % 2 === 1 ? '#ffffff' : '#f7f7f7',
                }}
              >
                <TableCell className="left-column-cell" style={{ backgroundColor: 'inherit' }}>
                  <Typography variant="body1" className="row-heading">
                    {row.label}
                  </Typography>
                </TableCell>
                <TableCell
                  className="subject-property-column"
                  style={{
                    backgroundColor: rowIndex % 2 === 1 ? 'lavender' : 'rgb(227, 227, 255)',
                  }}
                >
                  <Typography variant="body1">
                    {row.render(subjectProperty, subjectProperty, adjustments, handleAdjustmentSelect)}
                  </Typography>
                </TableCell>
                {finalSelectedHomes.map((home) => (
                  <TableCell key={home.id} style={{ backgroundColor: 'inherit' }}>
                    <Typography variant="body1">
                      {row.render(subjectProperty, home, adjustments, handleAdjustmentSelect)}
                    </Typography>
                  </TableCell>
                ))}
              </TableRow>
            ))}
          </TableBody> 
        </Table>
      </TableContainer>

      {step !== 3 && (
        <div className="plus-container" onClick={toggleDrawer ? toggleDrawer(true) : undefined}>
          <Typography variant="h1" className="plus-icon">+</Typography>
          <Typography variant="body2" className="guidance-text">Add Comparables</Typography>
        </div>
      )}

      {selectedHome && (
        <DetailedHomeView home={selectedHome} open={isModalOpen} onClose={handleModalClose} imagesHome={imagesByHome[selectedHome?.Ml_num] || []} />
      )}
      {comparableHome && (
        <CompareModal
          subjectProperty={subjectProperty}
          comparableHome={comparableHome}
          open={isCompareModalOpen}
          onClose={handleCompareModalClose}
          comparisonFields={comparisonFields}
          adjustments={adjustments[comparableHome.Ml_num]}
        />
      )}

      {/* Image Preview Dialog */}
      <Dialog open={!!selectedImage} onClose={() => setSelectedImage(null)} maxWidth="md">
        <DialogContent>
          <img src={selectedImage} alt="Selected Property" className="large-image-preview" />
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default ComparablesTable;
